import appSchema from '../schema/appSchema.js';
import { generateDeepDiffs } from './generateDeepDiffs.js';

const filterAppDiffs = (diffs = []) => {
  return diffs.filter((diff) => {
    const blacklistKeys = ['meta.changesSincePublish', 'meta.syncVersion', 'meta.qualityTests', 'version', 'updatedOn', 'notes'];

    return typeof diff?.key === 'string' && !blacklistKeys.some((key) => diff.key.startsWith(key));
  });
};

const filterActionDiffs = (diffs = []) => {
  return diffs.filter((diff) => {
    const blacklistKeys = ['meta.wizardMode', 'meta.wizardSection', 'meta.wizardItem', 'meta.wizardItemViews', 'meta.lastRunOn'];

    return typeof diff?.key === 'string' && !blacklistKeys.some((key) => diff.key.startsWith(key));
  });
};

const buildOperationDiffs = (prevObj, nextObj, type, schema = []) => {
  const addDiffs = nextObj
    ?.filter((item) => !prevObj?.find((previousItem) => item?.id === previousItem?.id))
    ?.map((item) => {
      return {
        type: `${type}Operation`,
        group: item?.name || schema?.find((schemaItem) => schemaItem?.type === item?.type)?.label,
        [`${type}Id`]: item?.id,
        operation: 'add',
        newValue: item
      };
    });

  const removeDiffs = prevObj
    ?.filter((item) => !nextObj?.find((nextItem) => nextItem?.id === item?.id))
    ?.map((item) => {
      return {
        type: `${type}Operation`,
        group: item?.name || schema?.find((schemaItem) => schemaItem?.type === item?.type)?.label,
        [`${type}Id`]: item?.id,
        operation: 'delete',
        previousValue: item
      };
    });

  const filteredPrev = prevObj?.filter((item) => !removeDiffs.some((diff) => diff?.[`${type}Id`] === item?.id));
  const filteredNext = nextObj?.filter((item) => !addDiffs.some((diff) => diff?.[`${type}Id`] === item?.id));

  return [addDiffs, removeDiffs, filteredPrev, filteredNext];
};

const buildChangesDiffs = (prevObj, nextObj, type, schema, baseKey) => {
  if (!Array.isArray(prevObj) || !Array.isArray(nextObj)) {
    return [];
  }

  const prevMap = new Map();
  const nextMap = new Map();

  for (const item of prevObj) {
    if (item?.id) {
      prevMap.set(item.id, item);
    }
  }

  for (const item of nextObj) {
    if (item?.id) {
      nextMap.set(item.id, item);
    }
  }

  const getGroup = (item) => {
    return item?.name || schema?.find((schemaItem) => schemaItem?.type === item?.type)?.label;
  };

  const getFullKey = (id, key) => {
    if (!key) {
      return `${baseKey}[id:${id}]`;
    }

    return `${baseKey}.${key}`.replace(/\.\[/g, '[');
  };

  const allDiffs = [];

  for (const [id, nextItem] of nextMap.entries()) {
    const prevItem = prevMap.get(id);

    if (!prevItem) {
      continue;
    }

    const itemDiffs = generateDeepDiffs(prevItem, nextItem, `[id:${id}]`);

    for (const diff of itemDiffs) {
      if (diff.operation === 'add' || diff.operation === 'delete') {
        continue;
      }

      const keyWithoutId = diff.key?.replace(/^\[id:[^\]]+\]\.?/, '') || diff.key;

      allDiffs.push({
        type,
        group: getGroup(nextItem),
        [`${type}Id`]: id,
        key: keyWithoutId,
        fullKey: getFullKey(id, diff.key),
        operation: 'change',
        previousValue: diff.previousValue,
        newValue: diff.newValue,
        newObject: nextItem
      });
    }
  }

  return allDiffs;
};

const normalizeAppForDiff = (app) => {
  if (!app) {
    return null;
  }

  const normalizeItems = (items) => items?.map((item) => ({ ...item, arguments: item.arguments || [] })) || [];

  return {
    ...app,
    authentication: normalizeItems(app.authentication),
    api: normalizeItems(app.api),
    actions: normalizeItems(app.actions)
  };
};

export const buildAppDiffs = (prevObj, nextObj) => {
  if (!nextObj || !prevObj) {
    return [];
  }

  const normalizedPrevObj = normalizeAppForDiff(prevObj);
  const normalizedNextObj = normalizeAppForDiff(nextObj);

  const { actions: prevActions, api: prevApi, authentication: prevAuth, ...prevApp } = normalizedPrevObj;
  const { actions: nextActions, api: nextApi, authentication: nextAuth, ...nextApp } = normalizedNextObj;

  const [addAuthOperationDiffs, removeAuthOperationDiffs, filteredPrevAuth, filteredNextAuth] = buildOperationDiffs(prevAuth, nextAuth, 'auth', appSchema?.authentication?.types);
  const [addApiOperationDiffs, removeApiOperationDiffs, filteredPrevApi, filteredNextApi] = buildOperationDiffs(prevApi, nextApi, 'api', appSchema?.api?.types);
  const [addActionOperationDiffs, removeActionOperationDiffs, filteredPrevActions, filteredNextActions] = buildOperationDiffs(prevActions, nextActions, 'action');

  const authDiffs = buildChangesDiffs(filteredPrevAuth, filteredNextAuth, 'auth', appSchema?.authentication?.types, 'authentication');
  const apiDiffs = buildChangesDiffs(filteredPrevApi, filteredNextApi, 'api', appSchema?.api?.types, 'api');
  const actionDiffs = filterActionDiffs(buildChangesDiffs(filteredPrevActions, filteredNextActions, 'action', [], 'actions'));
  const appDiffs = filterAppDiffs(generateDeepDiffs(prevApp, nextApp)).map((diff) => ({ ...diff, type: 'app', appId: prevApp?.id }));

  return [...appDiffs, ...addAuthOperationDiffs, ...removeAuthOperationDiffs, ...authDiffs, ...addApiOperationDiffs, ...removeApiOperationDiffs, ...apiDiffs, ...addActionOperationDiffs, ...removeActionOperationDiffs, ...actionDiffs].map((diff) => {
    const id = [diff.type, diff.appId, diff.authId, diff.apiId, diff.actionId, diff.key].filter(Boolean).join('-');
    return { id, ...diff };
  });
};
